import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { recentDraftListPath } from '../../services/config'
import { ancemntNotification, initNotification } from '../../redux/slices/announcementSlice'
import { FiZap } from 'react-icons/fi'
import { BsLightningChargeFill } from 'react-icons/bs'

const TEMPLATE_COLORS = [
  { bg: 'linear-gradient(135deg,#F9731615 0%,#EA580C08 100%)', border: '#F9731640', accent: '#F97316', btn: '#F9731620', btnBorder: '#F9731650', btnText: '#FDBA74' },
  { bg: 'linear-gradient(135deg,#EC489920 0%,#BE185D08 100%)', border: '#EC489940', accent: '#EC4899', btn: '#EC489920', btnBorder: '#EC489950', btnText: '#F9A8D4' },
  { bg: 'linear-gradient(135deg,#EAB30820 0%,#D9770608 100%)', border: '#EAB30840', accent: '#EAB308', btn: '#EAB30820', btnBorder: '#EAB30850', btnText: '#FDE047' },
  { bg: 'linear-gradient(135deg,#22C55E20 0%,#15803D08 100%)', border: '#22C55E40', accent: '#22C55E', btn: '#22C55E20', btnBorder: '#22C55E50', btnText: '#86EFAC' },
  { bg: 'linear-gradient(135deg,#8660F820 0%,#6D28D908 100%)', border: '#8660F840', accent: '#8660F8', btn: '#8660F820', btnBorder: '#8660F850', btnText: '#C4B5FD' },
  { bg: 'linear-gradient(135deg,#3B82F620 0%,#1D4ED808 100%)', border: '#3B82F640', accent: '#3B82F6', btn: '#3B82F620', btnBorder: '#3B82F650', btnText: '#93C5FD' },
]

const AnnouncemntTemplate = ({ selectTemplate }) => {
  const userData = JSON.parse(localStorage.getItem("user_data")) || {}
  const dispatch = useDispatch()
  const anStore = useSelector((state) => state.announce)
  const [templateList, setTemplateList] = useState([])
  const [hoveredIdx, setHoveredIdx] = useState(null)

  const getTemplateList = useCallback(() => {
    dispatch(ancemntNotification({
      path: recentDraftListPath,
      data: { userId: userData.user_id, isDraft: "2" }
    }))
  }, [])

  useEffect(() => { getTemplateList() }, [])
  useEffect(() => {
    if (anStore.ancemntNotificationSuccess) {
      setTemplateList(anStore.ancemntNotificationData || [])
      dispatch(initNotification())
    }
  }, [anStore])

  if (!templateList || templateList.length === 0) return null

  return (
    <div style={{ marginBottom: '32px' }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <div style={{
          width: 32, height: 32, borderRadius: '8px',
          background: 'linear-gradient(135deg,#8660F8,#c4b5fd)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <BsLightningChargeFill style={{ color: '#fff', fontSize: 15 }} />
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--primary-text-color)' }}>
            Announcement Templates
          </h3>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--secondary-text-color)' }}>
            Pre-designed templates to get you started quickly
          </p>
        </div>
      </div>

      {/* Scrollable template row */}
      <div style={{
        display: 'flex', gap: '14px', overflowX: 'auto',
        paddingBottom: '8px', paddingTop: '16px',
        scrollbarWidth: 'thin',
        scrollbarColor: '#8660F840 transparent'
      }}>
        {templateList.map((item, idx) => {
          const c = TEMPLATE_COLORS[idx % TEMPLATE_COLORS.length]
          const isHovered = hoveredIdx === idx
          return (
            <div
              key={item.announceId || idx}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{
                minWidth: '200px', maxWidth: '200px',
                background: c.bg,
                border: `1px solid ${isHovered ? c.accent : c.border}`,
                borderRadius: '14px',
                padding: '16px 14px',
                display: 'flex', flexDirection: 'column',
                gap: '10px', cursor: 'pointer', flexShrink: 0,
                transition: 'all 0.2s ease',
                transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
                boxShadow: isHovered ? `0 8px 24px ${c.accent}25` : '0 2px 8px rgba(0,0,0,0.15)',
                position: 'relative', overflow: 'hidden'
              }}
            >
              {/* Glow dot top-right */}
              <div style={{
                position: 'absolute', top: -20, right: -20,
                width: 60, height: 60, borderRadius: '50%',
                background: c.accent, opacity: 0.08, filter: 'blur(12px)'
              }} />

              {/* Icon */}
              <div style={{
                width: 44, height: 44, borderRadius: '10px',
                background: `${c.accent}20`,
                border: `1px solid ${c.accent}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '22px'
              }}>
                {item.announceIcon
                  ? <img src={item.announceIcon} style={{ width: 26, height: 26, objectFit: 'contain' }} />
                  : <FiZap style={{ color: c.accent }} />
                }
              </div>

              {/* Title & desc */}
              <div>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--primary-text-color)' }}>
                  {item.announceTypeName || 'Festival'}
                </h4>
                <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'var(--secondary-text-color)', lineHeight: 1.4 }}>
                  {item.announceDesc || 'Celebrate holidays'}
                </p>
              </div>

              {/* Stats */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'var(--secondary-text-color)' }}>Used</span>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: c.accent }}>24 times</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'var(--secondary-text-color)' }}>Updated</span>
                  <span style={{ fontSize: '11px', color: 'var(--secondary-text-color)' }}>2 days ago</span>
                </div>
              </div>

              {/* CTA button */}
              <button
                onClick={() => selectTemplate(item)}
                style={{
                  width: '100%', padding: '8px 0',
                  background: isHovered ? c.accent : c.btn,
                  border: `1px solid ${c.btnBorder}`,
                  borderRadius: '8px', cursor: 'pointer',
                  color: isHovered ? '#fff' : c.btnText,
                  fontSize: '12px', fontWeight: 600,
                  transition: 'all 0.2s ease',
                  letterSpacing: '0.02em'
                }}
              >
                Use Template
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default AnnouncemntTemplate
